/**
 * Chat Store — Zustand store for the AI chat panel.
 *
 * HANDOFF_AI_CHAT_FEATURE_V1_REVISED §8 (revised — standalone-only V1, but
 * the panel-shaped state is unchanged so V1.5 can mount the same store).
 *
 * Persistence: localStorage with a 24-hour TTL on `messages` + `dealContext`.
 * UI state (`isOpen`, `isStreaming`) is NOT persisted — those reset on refresh.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { Citation, CitationBlock } from '../features/chat/types';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  // Tool results received during streaming. Renderer maps tool_name to a
  // component via features/chat/lib/component-registry.
  toolResults?: Array<{ tool_name: string; result: unknown }>;
  /** V1.4.1 — internal resume messages emitted by UI control card
   * components (ChatDealCreationCard, ChatUploadCard, the two selection
   * cards) after a successful action. Sent to the backend so the model
   * has structured context, but never rendered in the chat thread —
   * filtered out in ChatMessage.tsx. */
  synthetic?: boolean;
  /** Citations Phase 2 — citations attached to this assistant turn.
   * Built from final_msg.content[i].citations on the backend; round-trips
   * through the SSE `citation` event live, and through
   * GET /chat/session/{id}/messages on session resume. Undefined for
   * user turns, pre-Phase-2 assistant turns, and assistant turns where
   * no citations landed. */
  citations?: CitationBlock[];
}

export interface ChatDealContext {
  deal_id: number;
  deal_name?: string;
  /** Citations Phase 1 — id of the latest ANALYZED term sheet for this
   * deal, populated by `useChatDealHydration`. Drives the "View term sheet"
   * link below assistant turns. NOT populated by any of the 6
   * `setDealContext` call sites — deliberately resolved lazily so the
   * cached value never goes stale. Null when the deal has no analyzed
   * term sheet yet. */
  active_term_sheet_id?: number;
}

/** HANDOFF_ACCOUNTANT_DASHBOARD_PHASE_F — advisor (accountant) client scope.
 * Set automatically from /portal/clients/:id by AccountantClientPage's
 * mount effect; cleared on unmount. Forwarded to the backend in the chat
 * POST body so chat_service can inject an advisor block into the system
 * prompt and the AI can call list_deals(client_id=...) on demand. */
export interface AdvisorContext {
  client_id: number;
  client_name: string;
  nace_code?: string | null;
}

/** Pending data-gap question emitted by the backend via a `gap_q` SSE event.
 * Mirrors the request_missing_information tool input schema. */
export interface GapQuestion {
  field: string;
  suggestion: string | null;
  source: string | null;
  blocking: boolean;
  prose: string;
}

/** Streaming phase for gating the skeleton indicator vs. live text.
 *  - 'idle'      → no in-flight request
 *  - 'thinking'  → request sent, waiting for first text/tool delta
 *  - 'streaming' → first delta received, content is appearing
 */
export type StreamingPhase = 'idle' | 'thinking' | 'streaming';

/** AI Chat V1.1 Item 6 — navigation CTA emitted via the navigate_to SSE
 * event. Rendered as a tappable chip below the assistant message; cleared
 * on click or when the user sends a new message.
 *
 * V1.3: `upload` and `deal_upload` destinations are retired — file upload
 * now flows through the inline ChatUploadCard (request_upload tool +
 * upload_prompt SSE event). navigate_to is reserved for views that need
 * the full app viewport. */
export interface NavigationCTA {
  destination: 'deals_list' | 'deal_detail' | 'analysis';
  label: string;
  deal_id?: number;
  reason?: string;
}

/** AI Chat V1.3 — inline upload card emitted via the upload_prompt SSE
 * event. Rendered as a file-picker card below the assistant message;
 * cleared after a successful upload or when the user sends a new
 * message. */
export interface UploadPrompt {
  deal_id: number;
  label: string;
  reason?: string;
}

/** In-flight term sheet upload that has handed off to the backend and is
 * being extracted. Set by ChatUploadCard the moment the file POST returns;
 * cleared once extraction reaches a terminal status (ANALYZED) and the
 * synthetic resume message has been sent. Drives the persistent loading
 * view inside the card so the user can close/reopen the chat without
 * losing progress feedback. */
export interface InflightUpload {
  termSheetId: number;
  fileName: string;
  dealId: number;
}

/** AI Chat V1.4 — model-extracted intent passed to the deal-creation card
 * as pre-fill. All fields optional; the card validates before submission. */
export interface DealCreationPrefill {
  deal_name?: string;
  financing_purpose?: string;
  financing_amount?: number;
  industry?: string;
  timeline_urgency?: string;
}

/** AI Chat V1.4 — deal-creation card emitted via the deal_creation_prompt
 * SSE event. Rendered as a structured-input card; cleared on successful
 * submit or when the user sends a new message. */
export interface DealCreationPrompt {
  prefill: DealCreationPrefill;
}

/** AI Chat V1.5 — one row in the inline deal picker (cold-session
 * disambiguation). Subset of the _list_deals payload. */
export interface SelectableDeal {
  id: number;
  deal_name?: string | null;
  financing_purpose?: string | null;
  financing_amount?: number | null;
  currency?: string | null;
  score?: number | null;
  bank_name?: string | null;
  status?: string | null;
  term_sheets_count?: number | null;
}

/** AI Chat V1.5 — deal picker emitted via the deal_selection_prompt SSE
 * event. Rendered as a list of clickable rows; clicking sets dealContext
 * and resumes the agent with a synthetic confirmation message. */
export interface DealSelectionPrompt {
  deals: SelectableDeal[];
  reason?: string;
}

/** AI Chat V1.5 — one row in the term-sheet picker. Backend returns
 * bank_id only; the card resolves bank_name client-side via useBanks(). */
export interface SelectableTermSheet {
  id: number;
  status: string;
  filename?: string | null;
  bank_id?: number | null;
  uploaded_at?: string | null;
  version_number?: number | null;
}

/** AI Chat V1.5 — term-sheet picker emitted via the
 * term_sheet_selection_prompt SSE event. Rendered when a deal has 2+
 * analyzed term sheets and the model needs to know which one. */
export interface TermSheetSelectionPrompt {
  deal_id: number;
  term_sheets: SelectableTermSheet[];
  reason?: string;
}

/** AI Chat V1.1 Item 4 — one row in the recent-chats sidebar/dropdown. */
export interface SessionMeta {
  id: string;
  title: string;
  dealId: number | null;
  dealName: string | null;
  language: string | null;
  startedAt: string;
  lastMessageAt: string | null;
  messageCount: number;
}

interface ChatState {
  isOpen: boolean;
  isStreaming: boolean;
  streamingPhase: StreamingPhase;
  messages: ChatMessage[];
  dealContext: ChatDealContext | null;
  // Phase F — advisor client scope (set on /portal/clients/:id mount).
  // Null for SME users and for advisors who haven't opened a client page.
  advisorContext: AdvisorContext | null;
  // ISO string of when `messages` was last touched. Used by the persist
  // hydrator to clear stale state past the 24h TTL.
  lastTouchedAt: string | null;

  // Last user-typed message; used by ChatErrorBanner's Retry button. Cleared
  // on successful [DONE], preserved on stream error so the user can retry.
  lastUserMessage: string | null;
  // Most recent error envelope from the stream ({"type":"error",...}).
  // Distinct from toast errors — this stays visible until cleared so the
  // Retry banner can render.
  error: string | null;
  // Pending gap question. Set when a `gap_q` SSE event arrives; cleared
  // when the user confirms (✓), corrects (✗), or sends any new message.
  gapQuestion: GapQuestion | null;
  // Once-per-session marker so the free-tier upsell line only renders the
  // first time a gap is shown. V1 is Pro-only by feature flag so this is
  // currently dormant — kept for V1.1 free tier.
  firstGapUpsellShown: boolean;

  // Server-issued chat session id (PHASE_E telemetry). Set when a
  // `session_assigned` SSE event arrives on the first turn (or after
  // /chat/session/start in V1.1). Cleared on `reset()`. Persisted via
  // partialize so a page reload within the 24h TTL keeps the session.
  sessionId: string | null;
  // Client-minted UUID, persisted across reloads (24h TTL same as
  // messages). Sent to backend as `client_session_id` for multi-tab
  // correlation; backend doesn't trust it for ownership.
  clientSessionId: string;

  // AI Chat V1.1 Item 4 — recent-chats sidebar.
  sessions: SessionMeta[];
  sessionsLoaded: boolean;
  isLoadingHistory: boolean;

  // AI Chat V1.1 Item 6 — pending navigation chip from a navigate_to SSE.
  // Transient — never persisted.
  pendingNavigation: NavigationCTA | null;

  // AI Chat V1.3 — pending inline-upload card from an upload_prompt SSE.
  // Transient — never persisted.
  uploadPrompt: UploadPrompt | null;

  // Term sheet upload currently being extracted. Set after the file POST
  // returns; cleared when extraction terminates. Transient — never
  // persisted; on a hard reload TanStack Query refetches status from the
  // backend and the dedicated /extraction page is the recovery surface.
  inflightUpload: InflightUpload | null;

  // AI Chat V1.4 — pending deal-creation card from a deal_creation_prompt
  // SSE event. Transient — never persisted.
  dealCreationPrompt: DealCreationPrompt | null;

  // AI Chat V1.5 — pending deal picker from a deal_selection_prompt SSE.
  // Transient — never persisted.
  dealSelectionPrompt: DealSelectionPrompt | null;

  // The deal the user picked from the selection card. Drives a persistent
  // compact confirmation that replaces the picker after selection. Transient.
  dealSelectionResolved: SelectableDeal | null;

  // AI Chat V1.5 — pending term-sheet picker from a
  // term_sheet_selection_prompt SSE. Transient — never persisted.
  termSheetSelectionPrompt: TermSheetSelectionPrompt | null;

  /** Pill-to-input prefill for the chat composer. A component-detail card's
   *  follow-up pill sets this; ChatInput consumes it into the textarea WITHOUT
   *  sending (the user confirms with Enter), then clears it. Transient. */
  inputDraft: string | null;

  open: () => void;
  close: () => void;
  toggle: () => void;
  setDealContext: (ctx: ChatDealContext | null) => void;
  // Phase F — advisor client scope. Both actions per the user's spec;
  // `clearAdvisorContext()` is the null-setter convenience.
  setAdvisorContext: (ctx: AdvisorContext | null) => void;
  clearAdvisorContext: () => void;

  appendMessage: (msg: ChatMessage) => void;
  appendDeltaToLastAssistant: (delta: string) => void;
  clearLastAssistantContent: () => void;
  replaceLastAssistantContent: (text: string) => void;
  appendToolResultToLastAssistant: (tr: { tool_name: string; result: unknown }) => void;
  /** Citations Phase 2 — append a citation that arrived via the SSE
   * `citation` event onto the last assistant message. Multiple citations
   * targeting the same `block_index` are collapsed under one
   * CitationBlock entry; new block_index values push a fresh entry. */
  appendCitationToLastAssistant: (entry: { block_index: number; citation: Citation }) => void;
  setMessages: (messages: ChatMessage[]) => void;
  setInputDraft: (text: string | null) => void;

  setStreaming: (streaming: boolean) => void;
  setStreamingPhase: (phase: StreamingPhase) => void;
  setLastUserMessage: (text: string | null) => void;
  setError: (message: string | null) => void;
  clearError: () => void;
  setGapQuestion: (gap: GapQuestion | null) => void;
  clearGapQuestion: () => void;
  markFirstGapUpsellShown: () => void;
  setSessionId: (id: string | null) => void;
  /** Clear the active conversation back to a fresh, sessionless state.
   *  Used by the desktop sidebar's "+ New chat" via useChatHistoryActions
   *  and by the mobile Header's New Chat button. Note: this does NOT abort
   *  an in-flight stream — the AbortController lives in useChatStream.
   *  Callers that need to cancel a stream should do so explicitly. */
  resetSession: () => void;

  /** Canonical conversation reset — null every conversation/turn-scoped
   *  field so a new or switched chat carries no context from the previous
   *  one (see CONVERSATION_RESET). Single source of truth: handleNewChat,
   *  handleSelectSession, and resetSession all route through it. Deliberately
   *  preserves `advisorContext` (page-scoped, owned by AccountantClientPage).
   *  Like resetSession, this does NOT abort an in-flight stream. */
  clearConversationState: () => void;

  /** Mobile recent-chats slide-over drawer state. The trigger lives in
   *  the Header (mobile only); the drawer renders inside ChatPage so it
   *  has access to useChatHistoryActions' handlers (cancel + select). */
  historyDrawerOpen: boolean;
  openHistoryDrawer: () => void;
  closeHistoryDrawer: () => void;

  /** True while the chat textarea has focus. Used by Header to force-
   *  hide itself on mobile during "type mode" so the sticky banner
   *  doesn't block the first message after send. */
  chatInputFocused: boolean;
  setChatInputFocused: (focused: boolean) => void;

  // Recent-chats actions
  setSessions: (sessions: SessionMeta[]) => void;
  upsertSession: (session: SessionMeta) => void;
  removeSession: (sessionId: string) => void;
  updateSessionTitle: (sessionId: string, title: string) => void;
  setSessionsLoaded: (loaded: boolean) => void;
  setLoadingHistory: (loading: boolean) => void;

  // Navigation chip actions (Item 6)
  setPendingNavigation: (nav: NavigationCTA | null) => void;
  clearPendingNavigation: () => void;

  // Upload card actions (V1.3)
  setUploadPrompt: (prompt: UploadPrompt | null) => void;
  clearUploadPrompt: () => void;
  setInflightUpload: (upload: InflightUpload) => void;
  clearInflightUpload: () => void;

  // Deal-creation card actions (V1.4)
  setDealCreationPrompt: (prompt: DealCreationPrompt | null) => void;
  clearDealCreationPrompt: () => void;

  // Selection card actions (V1.5)
  setDealSelectionPrompt: (prompt: DealSelectionPrompt | null) => void;
  clearDealSelectionPrompt: () => void;
  resolveDealSelection: (deal: SelectableDeal) => void;
  setTermSheetSelectionPrompt: (prompt: TermSheetSelectionPrompt | null) => void;
  clearTermSheetSelectionPrompt: () => void;

  reset: () => void;
}

const TTL_MS = 24 * 60 * 60 * 1000;

const newId = () =>
  // Crypto-randomUUID is available in modern browsers; fall back to a stamp.
  (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    ? crypto.randomUUID()
    : `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

/** Every conversation/turn-scoped field, reset to its initial value when a
 *  chat begins or switches. This is the single source of truth that prevents
 *  context from one conversation leaking into the next — add new transient
 *  chat fields here and every entry point (new chat / switch / reset) inherits
 *  the clear.
 *
 *  Deliberately EXCLUDES:
 *   - `advisorContext` — page-scoped, set/cleared by AccountantClientPage on
 *     mount/unmount; must survive a new chat so an advisor keeps client scope.
 *   - `clientSessionId`, `sessions`, `sessionsLoaded` — cross-conversation.
 *   - `isOpen`, `historyDrawerOpen`, `chatInputFocused`, streaming flags — UI
 *     / stream lifecycle, owned elsewhere.
 */
const CONVERSATION_RESET: Partial<ChatState> = {
  messages: [],
  sessionId: null,
  dealContext: null,
  error: null,
  gapQuestion: null,
  lastUserMessage: null,
  firstGapUpsellShown: false,
  pendingNavigation: null,
  uploadPrompt: null,
  inflightUpload: null,
  dealCreationPrompt: null,
  dealSelectionPrompt: null,
  dealSelectionResolved: null,
  termSheetSelectionPrompt: null,
  inputDraft: null,
};

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      isOpen: false,
      isStreaming: false,
      streamingPhase: 'idle',
      messages: [],
      dealContext: null,
      advisorContext: null,  // Phase F
      lastTouchedAt: null,
      lastUserMessage: null,
      error: null,
      gapQuestion: null,
      firstGapUpsellShown: false,
      sessionId: null,
      clientSessionId: newId(),  // minted on store init; persisted
      sessions: [],
      sessionsLoaded: false,
      isLoadingHistory: false,
      pendingNavigation: null,
      uploadPrompt: null,
      inflightUpload: null,
      dealCreationPrompt: null,
      dealSelectionPrompt: null,
      dealSelectionResolved: null,
      termSheetSelectionPrompt: null,
      inputDraft: null,

      open: () => set({ isOpen: true }),
      close: () => set({ isOpen: false }),
      toggle: () => set({ isOpen: !get().isOpen }),
      setDealContext: (ctx) => set({ dealContext: ctx, lastTouchedAt: new Date().toISOString() }),
      // Phase F — advisor client scope; ride the same lastTouchedAt TTL bump
      // as dealContext so the 24h persistence window stays consistent.
      setAdvisorContext: (ctx) =>
        set({ advisorContext: ctx, lastTouchedAt: new Date().toISOString() }),
      clearAdvisorContext: () => set({ advisorContext: null }),

      appendMessage: (msg) =>
        set((s) => ({
          messages: [...s.messages, { ...msg, id: msg.id || newId() }],
          lastTouchedAt: new Date().toISOString(),
        })),

      setInputDraft: (text) => set({ inputDraft: text }),

      appendDeltaToLastAssistant: (delta) =>
        set((s) => {
          const messages = [...s.messages];
          const last = messages[messages.length - 1];
          if (last && last.role === 'assistant') {
            messages[messages.length - 1] = { ...last, content: last.content + delta };
          } else {
            messages.push({ id: newId(), role: 'assistant', content: delta });
          }
          return { messages, lastTouchedAt: new Date().toISOString() };
        }),

      clearLastAssistantContent: () =>
        set((s) => {
          const messages = [...s.messages];
          const last = messages[messages.length - 1];
          if (last && last.role === 'assistant') {
            messages[messages.length - 1] = { ...last, content: '' };
          }
          return { messages, lastTouchedAt: new Date().toISOString() };
        }),

      replaceLastAssistantContent: (text) =>
        set((s) => {
          const messages = [...s.messages];
          const last = messages[messages.length - 1];
          if (last && last.role === 'assistant') {
            messages[messages.length - 1] = { ...last, content: text };
          } else {
            messages.push({ id: newId(), role: 'assistant', content: text });
          }
          return { messages, lastTouchedAt: new Date().toISOString() };
        }),

      appendToolResultToLastAssistant: (tr) =>
        set((s) => {
          const messages = [...s.messages];
          const last = messages[messages.length - 1];
          if (last && last.role === 'assistant') {
            messages[messages.length - 1] = {
              ...last,
              toolResults: [...(last.toolResults ?? []), tr],
            };
          } else {
            messages.push({ id: newId(), role: 'assistant', content: '', toolResults: [tr] });
          }
          return { messages, lastTouchedAt: new Date().toISOString() };
        }),

      appendCitationToLastAssistant: (entry) =>
        set((s) => {
          const messages = [...s.messages];
          const lastIdx = messages.length - 1;
          const last = messages[lastIdx];
          const target =
            last && last.role === 'assistant'
              ? last
              : { id: newId(), role: 'assistant' as const, content: '' };
          const existing = target.citations ?? [];
          const bucketIdx = existing.findIndex(
            (b) => b.block_index === entry.block_index,
          );
          let nextCitations: CitationBlock[];
          if (bucketIdx === -1) {
            nextCitations = [
              ...existing,
              { block_index: entry.block_index, citations: [entry.citation] },
            ];
          } else {
            nextCitations = existing.map((b, i) =>
              i === bucketIdx
                ? { ...b, citations: [...b.citations, entry.citation] }
                : b,
            );
          }
          const updated = { ...target, citations: nextCitations };
          if (last && last.role === 'assistant') {
            messages[lastIdx] = updated;
          } else {
            messages.push(updated);
          }
          return { messages, lastTouchedAt: new Date().toISOString() };
        }),

      setMessages: (messages) =>
        set({ messages, lastTouchedAt: new Date().toISOString() }),

      setStreaming: (streaming) => set({ isStreaming: streaming }),
      setStreamingPhase: (phase) =>
        set({ streamingPhase: phase, isStreaming: phase !== 'idle' }),
      setLastUserMessage: (text) => set({ lastUserMessage: text }),
      setError: (message) => set({ error: message }),
      clearError: () => set({ error: null }),
      setGapQuestion: (gap) => set({ gapQuestion: gap }),
      clearGapQuestion: () => set({ gapQuestion: null }),
      markFirstGapUpsellShown: () => set({ firstGapUpsellShown: true }),
      setSessionId: (id) => set({ sessionId: id }),
      // Mobile Header "new chat" entry point. Routes through the canonical
      // clear so it stays in lock-step with the desktop new-chat / switch
      // paths. Preserves advisorContext (page-scoped) — reset() is the hard
      // nuke that also drops it.
      resetSession: () => get().clearConversationState(),
      clearConversationState: () => set({ ...CONVERSATION_RESET }),

      // Mobile recent-chats drawer
      historyDrawerOpen: false,
      openHistoryDrawer: () => set({ historyDrawerOpen: true }),
      closeHistoryDrawer: () => set({ historyDrawerOpen: false }),

      // Chat textarea focus signal (drives mobile Header force-hide)
      chatInputFocused: false,
      setChatInputFocused: (focused: boolean) => set({ chatInputFocused: focused }),

      // Recent-chats actions
      setSessions: (sessions) => set({ sessions, sessionsLoaded: true }),
      upsertSession: (session) =>
        set((s) => {
          const existing = s.sessions.findIndex((x) => x.id === session.id);
          if (existing === -1) {
            return { sessions: [session, ...s.sessions] };
          }
          const next = [...s.sessions];
          next[existing] = { ...next[existing], ...session };
          return { sessions: next };
        }),
      removeSession: (sessionId) =>
        set((s) => ({ sessions: s.sessions.filter((x) => x.id !== sessionId) })),
      updateSessionTitle: (sessionId, title) =>
        set((s) => ({
          sessions: s.sessions.map((x) =>
            x.id === sessionId ? { ...x, title } : x
          ),
        })),
      setSessionsLoaded: (loaded) => set({ sessionsLoaded: loaded }),
      setLoadingHistory: (loading) => set({ isLoadingHistory: loading }),

      setPendingNavigation: (nav) => set({ pendingNavigation: nav }),
      clearPendingNavigation: () => set({ pendingNavigation: null }),

      setUploadPrompt: (prompt) => set({ uploadPrompt: prompt }),
      clearUploadPrompt: () => set({ uploadPrompt: null, inflightUpload: null }),
      setInflightUpload: (upload) => set({ inflightUpload: upload }),
      clearInflightUpload: () => set({ inflightUpload: null }),

      setDealCreationPrompt: (prompt) => set({ dealCreationPrompt: prompt }),
      clearDealCreationPrompt: () => set({ dealCreationPrompt: null }),

      setDealSelectionPrompt: (prompt) =>
        set({ dealSelectionPrompt: prompt, dealSelectionResolved: null }),
      clearDealSelectionPrompt: () => set({ dealSelectionPrompt: null }),
      resolveDealSelection: (deal) =>
        set({ dealSelectionResolved: deal, dealSelectionPrompt: null }),

      setTermSheetSelectionPrompt: (prompt) =>
        set({ termSheetSelectionPrompt: prompt }),
      clearTermSheetSelectionPrompt: () =>
        set({ termSheetSelectionPrompt: null }),

      reset: () =>
        set({
          messages: [],
          dealContext: null,
          advisorContext: null,  // Phase F
          lastTouchedAt: null,
          lastUserMessage: null,
          error: null,
          gapQuestion: null,
          firstGapUpsellShown: false,
          pendingNavigation: null,
          uploadPrompt: null,
          inflightUpload: null,
          dealCreationPrompt: null,
          dealSelectionPrompt: null,
          dealSelectionResolved: null,
          termSheetSelectionPrompt: null,
          // sessionId cleared so the next /chat call mints a fresh server
          // session. clientSessionId stays — it groups multi-tab usage and
          // survives the reset on purpose.
          sessionId: null,
        }),
    }),
    {
      name: 'credia-chat',
      partialize: (state) => ({
        // V1.1 Item 4: messages are no longer persisted to localStorage —
        // the backend is the source of truth and the frontend re-fetches
        // them via GET /chat/session/{id}/messages on session restore.
        dealContext: state.dealContext,
        // Phase F — persist advisor client scope on the same 24h TTL so a
        // refresh while on /portal/clients/:id keeps the context.
        advisorContext: state.advisorContext,
        lastTouchedAt: state.lastTouchedAt,
        // Persist session ids across reloads within the 24h TTL so the
        // sidebar can re-select the active session and telemetry stays
        // grouped across a page refresh.
        sessionId: state.sessionId,
        clientSessionId: state.clientSessionId,
      }),
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<ChatState>;
        // Apply 24h TTL to persisted state. After expiry, drop the active
        // session pointer so we don't try to restore a stale conversation.
        if (p.lastTouchedAt) {
          const age = Date.now() - new Date(p.lastTouchedAt).getTime();
          if (Number.isFinite(age) && age > TTL_MS) {
            return {
              ...current,
              dealContext: null,
              advisorContext: null,  // Phase F — drop stale advisor scope too
              lastTouchedAt: null,
              sessionId: null,
            };
          }
        }
        return { ...current, ...p };
      },
    }
  )
);

